import { useConsumeOrganisationSequence } from '@my/directory-api/hooks';
import type { Order } from '@my/tms-api';
import { cn, showToast } from '@my/ui';
import { Copy, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CopyFromOrderPanel } from './CopyFromOrderPanel';

/**
 * Copy an existing order into a new one — the same choices as New order's
 * "Start from a previous order", the same backend (`POST orders/copy`). Opened
 * from Order Details (Actions → Copy) and from the Orders list's row menu.
 *
 * <p>
 * The new order's number is minted only when the copy is made, so opening and
 * closing the dialog burns none. The operator stays where they are; a message
 * names the new order. Copies the order as last SAVED.
 * </p>
 */
export function CopyOrderDialog({
  order,
  organisationId,
  open,
  onClose,
}: {
  order: Order | null;
  organisationId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const consumeSequence = useConsumeOrganisationSequence();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !order || !organisationId || typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 p-4 pt-12"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="copy-order-dialog-title"
        className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-md bg-white shadow-xl"
      >
        <header className="flex items-start justify-between gap-2 border-b border-slate-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <Copy className="h-4 w-4 text-slate-400" />
            <div>
              <h3 id="copy-order-dialog-title" className="text-sm font-semibold text-slate-900">
                Copy {order.orderNumber}
              </h3>
              <p className="text-[11px] text-slate-500">
                A new order from this one, as last saved. It gets its own number when you create it.
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="min-h-0 overflow-auto p-3">
          <CopyFromOrderPanel
            // Fresh choices for each order copied.
            key={order.id}
            organisationId={organisationId}
            order={order}
            actionLabel="Create copy"
            newOrder={{
              orderNumber: '',
              allocateOrderNumber: async () =>
                (await consumeSequence.mutateAsync({ organisationId, type: 'Order' })).formatted,
              // The copied order's own currency (the server keeps it when none is sent).
              currencyIsoCode: null,
              // Nothing typed here: with References ticked the copy fills them all.
              references: {
                reference1: null,
                reference2: null,
                reference3: null,
                reference4: null,
                additionalReferencesJson: null,
              },
            }}
            onCopied={(created) => {
              onClose();
              showToast({
                type: 'success',
                message: `Order ${created.orderNumber} created as a copy of ${order.orderNumber}.`,
                // Long enough to note the number down.
                durationMs: 10000,
              });
            }}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Order Details → Actions: the Copy button beside History. */
export function CopyOrderButton({
  order,
  organisationId,
}: {
  order: Order | null;
  organisationId: string | null;
}) {
  const [open, setOpen] = useState(false);

  if (!order || !organisationId) return null;

  return (
    <>
      <button
        type="button"
        aria-label="Copy order"
        title="Copy this order into a new one"
        onClick={() => setOpen(true)}
        className={cn(
          // Same 28px square as History and the move button beside it.
          'inline-flex h-7 w-7 items-center justify-center rounded-md',
          'border border-slate-300 bg-white text-slate-500',
          'hover:bg-slate-50 hover:text-slate-700',
        )}
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
      <CopyOrderDialog
        order={order}
        organisationId={organisationId}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
